import React, { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    shift,
    useFloating,
    useTransitionStyles,
} from '@floating-ui/react';
import { createUseStyles, Text, useTheme } from '@Sage/hoi-poi-ui';

import { attachSelectionLimitTableListeners } from './attachSelectionLimitTableListeners.js';
import { createTopCenterReference } from './selectionLimitPopoverUtils.js';
import styles from './styles.js';

const useStyles = createUseStyles(styles, 'SelectionLimitPopover');

const TABLE_ID = 's200uki-company-selection';
const CLOSE_DELAY_MS = 200;

const SelectionLimitPopover = ({ checkboxColId, content, isEnabled }) => {
    const theme = useTheme();
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const activeTriggerRef = useRef(null);

    const { refs, floatingStyles, context } = useFloating({
        open,
        placement: 'top-end',
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: 200,
        initial: { opacity: 0 },
        open: { opacity: 1 },
        close: { opacity: 0 },
    });

    useLayoutEffect(() => {
        if (!open || !activeTriggerRef.current) return;

        refs.setReference(createTopCenterReference(activeTriggerRef.current));
    }, [open, refs]);

    useEffect(() => {
        if (!isEnabled || !content) {
            setOpen(false);
            activeTriggerRef.current = null;
            return undefined;
        }

        const tableEl = document.getElementById(TABLE_ID);
        if (!tableEl) return undefined;

        return attachSelectionLimitTableListeners({
            tableEl,
            checkboxColId,
            closeDelayMs: CLOSE_DELAY_MS,
            onOpen: (trigger) => {
                activeTriggerRef.current = trigger;
                setOpen(true);
            },
            onClose: () => {
                setOpen(false);
                activeTriggerRef.current = null;
            },
        });
    }, [checkboxColId, content, isEnabled]);

    if (!isEnabled || !content) return null;

    return (
        <FloatingPortal>
            {isMounted && open && (
                <div
                    ref={refs.setFloating}
                    data-selection-limit-popover
                    className={classes.root}
                    style={{ ...floatingStyles, ...transitionStyles }}
                >
                    <div className={classes.content}>
                        <Text type="body" color={theme.colors.utility.textPrimary}>
                            {content}
                        </Text>
                    </div>
                </div>
            )}
        </FloatingPortal>
    );
};

SelectionLimitPopover.propTypes = {
    checkboxColId: PropTypes.string.isRequired,
    content: PropTypes.string,
    isEnabled: PropTypes.bool,
};

SelectionLimitPopover.displayName = 'SelectionLimitPopover';

export default memo(SelectionLimitPopover);
